import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[] | null;
}

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  ogImage = "https://werbekreis-roding.de/werbekreis-logo.png",
  jsonLd = null
}: SEOProps) => {
  useEffect(() => {
    // Set document title
    document.title = title;

    // Remove existing meta tags
    const existingMetas = document.querySelectorAll('meta[data-dynamic="true"]');
    existingMetas.forEach(meta => meta.remove());

    // Helper function to create meta tags
    const createMeta = (property: string, content: string, isProperty = false) => {
      const meta = document.createElement('meta');
      if (isProperty) {
        meta.setAttribute('property', property);
      } else {
        meta.setAttribute('name', property);
      }
      meta.setAttribute('content', content);
      meta.setAttribute('data-dynamic', 'true');
      document.head.appendChild(meta);
    };

    // Basic meta tags
    createMeta('description', description);
    
    if (keywords) {
      createMeta('keywords', keywords);
    }

    // Open Graph tags
    createMeta('og:title', title, true);
    createMeta('og:description', description, true);
    createMeta('og:type', 'website', true);
    createMeta('og:image', ogImage, true);
    createMeta('og:site_name', 'Werbekreis Roding', true);

    // Twitter tags
    createMeta('twitter:card', 'summary_large_image');
    createMeta('twitter:title', title);
    createMeta('twitter:description', description);
    createMeta('twitter:image', ogImage);

    // Canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', canonical);
    }

    // Structured data (JSON-LD)
    document.querySelectorAll('script[data-dynamic-jsonld="true"]').forEach(s => s.remove());
    if (jsonLd) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-dynamic-jsonld', 'true');
      script.text = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      const dynamicMetas = document.querySelectorAll('meta[data-dynamic="true"]');
      dynamicMetas.forEach(meta => meta.remove());
      document.querySelectorAll('script[data-dynamic-jsonld="true"]').forEach(s => s.remove());
    };
  }, [title, description, keywords, canonical, ogImage, jsonLd]);
};